import { Article, HandlingUnit, isCargoFreight, Shipment, TransportOrder } from "@/model/transportorder/transportOrder"
import { IntlShape } from "react-intl"
import {
    aggregateNetExplosiveMassInG,
    calculateVolume,
    calculateWeight,
    CsvHeader,
    downloadCsv,
    hasCustomsGoods,
    toHumanReadableDate,
    toHumanReadableTime
} from "@/components/TransportOrder/CSVExport/csvExportHelpers"
import { getArticlesFromHandlingUnit } from "@/components/TransportOrder/CSVExport/ArticleExtractor"
import { articleIsDangerousGood } from "@/model/transportorder/transportOrderDangerousGoods"
import { getClientTimezone, getTimezoneOfCountry } from "@/timezones/timezones"
import { normalizeToMmt } from "@/model/transportorder/measurement"

interface MaterialCsvExportRow {
    shipmentExternalIdentification?: string
    referenceSupplier?: string
    externalOrderCreatedDate?: string
    pickUpDate?: string
    from?: string
    to?: string
    deliveryDate?: string
    senderName?: string
    senderCountry?: string
    senderPostalCode?: string
    senderCity?: string
    senderStreet?: string
    senderDUNS?: string
    senderLNo?: string
    buyerName?: string
    buyerCountry?: string
    buyerZip?: string
    buyerCity?: string
    buyerStreet?: string
    plantCustomer?: string
    recipientCID3?: string
    unloadingPoint?: string
    recipientName?: string
    recipientCountry?: string
    recipientZip?: string
    recipientCity?: string
    recipientStreet?: string
    productNumber?: string
    lengthHandlingUnit?: number
    widthHandlingUnit?: number
    heightHandlingUnit?: number
    numberOfPcc?: number
    innerPackagingMaterialId?: string
    handlingUnitQuantity?: number
    outerPackagingMaterialId?: string
    nrPackagingAid1?: string
    packagingAid1?: string
    nrPackagingAid2?: string
    packagingAid2?: string
    nrPackagingAid3?: string
    packagingAid3?: string
    unDangerousGoodsCode?: string
    descriptionDangerousGoods?: string
    dangerousGoodsClass?: string
    dangerousGoodsPackagingGroup?: string
    netExplosiveWeight?: number
    customsGoods?: string
    grossWeight?: number
    grossVolume?: number
    stackingFactor?: number
    orderNumber?: string
    transportNumber?: string
}

export const downloadMaterialsCsv = (
    bundles: TransportOrder[],
    filename: string,
    intl: IntlShape,
): void => {
    const headers: CsvHeader<MaterialCsvExportRow>[] = [
        {
            key: "shipmentExternalIdentification",
            label: intl.formatMessage({ id: "csvExport.headers.material.shipmentExternalIdentification" })
        },
        { key: "referenceSupplier", label: intl.formatMessage({ id: "csvExport.headers.material.referenceSupplier" }) },
        { key: "externalOrderCreatedDate", label: intl.formatMessage({ id: "csvExport.headers.material.externalOrderCreatedDate" }) },
        { key: "pickUpDate", label: intl.formatMessage({ id: "csvExport.headers.material.pickUpDate" }) },
        { key: "from", label: intl.formatMessage({ id: "csvExport.headers.material.from" }) },
        { key: "to", label: intl.formatMessage({ id: "csvExport.headers.material.to" }) },
        { key: "deliveryDate", label: intl.formatMessage({ id: "csvExport.headers.material.deliveryDate" }) },
        { key: "senderName", label: intl.formatMessage({ id: "csvExport.headers.material.senderName" }) },
        { key: "senderCountry", label: intl.formatMessage({ id: "csvExport.headers.material.senderCountry" }) },
        { key: "senderPostalCode", label: intl.formatMessage({ id: "csvExport.headers.material.senderPostalCode" }) },
        { key: "senderCity", label: intl.formatMessage({ id: "csvExport.headers.material.senderCity" }) },
        { key: "senderStreet", label: intl.formatMessage({ id: "csvExport.headers.material.senderStreet" }) },
        { key: "senderDUNS", label: intl.formatMessage({ id: "csvExport.headers.material.senderDUNS" }) },
        { key: "senderLNo", label: intl.formatMessage({ id: "csvExport.headers.material.senderLNo" }) },
        { key: "buyerName", label: intl.formatMessage({ id: "csvExport.headers.material.buyerName" }) },
        { key: "buyerCountry", label: intl.formatMessage({ id: "csvExport.headers.material.buyerCountry" }) },
        { key: "buyerZip", label: intl.formatMessage({ id: "csvExport.headers.material.buyerZip" }) },
        { key: "buyerCity", label: intl.formatMessage({ id: "csvExport.headers.material.buyerCity" }) },
        { key: "buyerStreet", label: intl.formatMessage({ id: "csvExport.headers.material.buyerStreet" }) },
        { key: "plantCustomer", label: intl.formatMessage({ id: "csvExport.headers.material.plantCustomer" }) },
        { key: "recipientCID3", label: intl.formatMessage({ id: "csvExport.headers.material.recipientCID3" }) },
        { key: "unloadingPoint", label: intl.formatMessage({ id: "csvExport.headers.material.unloadingPoint" }) },
        { key: "recipientName", label: intl.formatMessage({ id: "csvExport.headers.material.recipientName" }) },
        { key: "recipientCountry", label: intl.formatMessage({ id: "csvExport.headers.material.recipientCountry" }) },
        { key: "recipientZip", label: intl.formatMessage({ id: "csvExport.headers.material.recipientZip" }) },
        { key: "recipientCity", label: intl.formatMessage({ id: "csvExport.headers.material.recipientCity" }) },
        { key: "recipientStreet", label: intl.formatMessage({ id: "csvExport.headers.material.recipientStreet" }) },
        { key: "productNumber", label: intl.formatMessage({ id: "csvExport.headers.material.productNumber" }) },
        { key: "lengthHandlingUnit", label: intl.formatMessage({ id: "csvExport.headers.material.lengthHandlingUnit" }) },
        { key: "widthHandlingUnit", label: intl.formatMessage({ id: "csvExport.headers.material.widthHandlingUnit" }) },
        { key: "heightHandlingUnit", label: intl.formatMessage({ id: "csvExport.headers.material.heightHandlingUnit" }) },
        { key: "numberOfPcc", label: intl.formatMessage({ id: "csvExport.headers.material.numberOfPcc" }) },
        { key: "innerPackagingMaterialId", label: intl.formatMessage({ id: "csvExport.headers.material.innerPackagingMaterialId" }) },
        { key: "handlingUnitQuantity", label: intl.formatMessage({ id: "csvExport.headers.material.handlingUnitQuantity" }) },
        { key: "outerPackagingMaterialId", label: intl.formatMessage({ id: "csvExport.headers.material.outerPackagingMaterialId" }) },
        { key: "nrPackagingAid1", label: intl.formatMessage({ id: "csvExport.headers.material.nrPackagingAid1" }) },
        { key: "packagingAid1", label: intl.formatMessage({ id: "csvExport.headers.material.packagingAid1" }) },
        { key: "nrPackagingAid2", label: intl.formatMessage({ id: "csvExport.headers.material.nrPackagingAid2" }) },
        { key: "packagingAid2", label: intl.formatMessage({ id: "csvExport.headers.material.packagingAid2" }) },
        { key: "nrPackagingAid3", label: intl.formatMessage({ id: "csvExport.headers.material.nrPackagingAid3" }) },
        { key: "packagingAid3", label: intl.formatMessage({ id: "csvExport.headers.material.packagingAid3" }) },
        { key: "unDangerousGoodsCode", label: intl.formatMessage({ id: "csvExport.headers.material.unDangerousGoodsCode" }) },
        { key: "descriptionDangerousGoods", label: intl.formatMessage({ id: "csvExport.headers.material.descriptionDangerousGoods" }) },
        { key: "dangerousGoodsClass", label: intl.formatMessage({ id: "csvExport.headers.material.dangerousGoodsClass" }) },
        {
            key: "dangerousGoodsPackagingGroup",
            label: intl.formatMessage({ id: "csvExport.headers.material.dangerousGoodsPackagingGroup" })
        },
        { key: "netExplosiveWeight", label: intl.formatMessage({ id: "csvExport.headers.material.netExplosiveWeight" }) },
        { key: "customsGoods", label: intl.formatMessage({ id: "csvExport.headers.material.customsGoods" }) },
        { key: "grossWeight", label: intl.formatMessage({ id: "csvExport.headers.material.grossWeight" }) },
        { key: "grossVolume", label: intl.formatMessage({ id: "csvExport.headers.material.grossVolume" }) },
        { key: "stackingFactor", label: intl.formatMessage({ id: "csvExport.headers.material.stackingFactor" }) },
        { key: "orderNumber", label: intl.formatMessage({ id: "csvExport.headers.material.orderNumber" }) },
        { key: "transportNumber", label: intl.formatMessage({ id: "csvExport.headers.material.transportNumber" }) },
    ]
    const rows = bundles.flatMap(bundle => mapBundleToCsvData(bundle, intl))
    downloadCsv(rows, headers, filename, intl)
}

export const mapBundleToCsvData = (bundle: TransportOrder, intl: IntlShape): MaterialCsvExportRow[] => {
    if (!bundle._embedded) {
        return []
    }
    return bundle._embedded.shipments
        .flatMap(shipment => mapOrderToCsvData(shipment, bundle, intl))
}

const mapOrderToCsvData = (shipment: Shipment, transportOrder: TransportOrder, intl: IntlShape): MaterialCsvExportRow[] => {
    if (!isCargoFreight(shipment.freight)) {
        return [mapHandlingUnitToCsvData(undefined, shipment, transportOrder, intl)]
    }
    return shipment.freight.handling_units
        .map(handlingUnit => mapHandlingUnitToCsvData(handlingUnit, shipment, transportOrder, intl))
}

const distinct = (value: string | null | undefined, index: number, self: Array<string | null | undefined>): boolean => {
    return self.indexOf(value) === index
}

const getInternalPlaceOfDestination = (articles: Article[]): string | undefined => {
    return articles.map(articles => articles.internal_place_of_destination)
        .filter(internalDestination => !!internalDestination)
        .filter(distinct)
        .join(" / ")
}

const mapHandlingUnitToCsvData = (handlingUnit: HandlingUnit | undefined, shipment: Shipment, transportOrder: TransportOrder, intl: IntlShape): MaterialCsvExportRow => {
    const articles = getArticlesFromHandlingUnit(handlingUnit)

    const productNumber = articles
        .map(article => article.article_id)
        .find(id => (id !== undefined && id !== null))

    const unDangerousGoodsCode = articles.filter(articleIsDangerousGood)
        .map(article => article.dangerous_goods_information?.undg_code)
        .filter(undgCode => !!undgCode)
        .filter(distinct)
        .join(" / ")

    const currentTransportLeg = shipment.currentTransportLeg
    const startOfTransportChain = shipment.startOfTransportChain
    const endOfTransportChain = shipment.endOfTransportChain

    const pickupTimeWindow = currentTransportLeg.departureRequestedAt ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.pickupRequestedAt : undefined)
    const senderAddress = currentTransportLeg.placeOfDeparture.address ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.address : undefined)
    const senderTimezone = getTimezoneOfCountry(senderAddress?.country)
    const senderName = currentTransportLeg.placeOfDeparture.name ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.name : undefined)
    const senderDuns = currentTransportLeg.placeOfDeparture.externalIdentifiers.duns ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.externalIdentifiers.duns : undefined)
    const senderSupplierCode = currentTransportLeg.placeOfDeparture.externalIdentifiers.supplierCode ??
        (shipment.isStartOfTransportChain ? startOfTransportChain.consignor.externalIdentifiers.supplierCode : undefined)

    const recipientTimeWindow = currentTransportLeg.arrivalEstimatedAt ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.deliveryEstimatedAt : undefined)
    const recipientAddress = currentTransportLeg.placeOfArrival.address ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.address : undefined)
    const recipientName = currentTransportLeg.placeOfArrival.name ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.name : undefined)
    const recipientTimezone = getTimezoneOfCountry(recipientAddress?.country)
    const recipientPlantCode = currentTransportLeg.placeOfArrival.externalIdentifiers.plantCode ??
        (shipment.isEndOfTransportChain ? endOfTransportChain.consignee.externalIdentifiers.plantCode : undefined)

    return {
        shipmentExternalIdentification: shipment.external_identification,
        referenceSupplier: shipment.isStartOfTransportChain ? startOfTransportChain.accessCode : undefined,
        externalOrderCreatedDate: toHumanReadableDate(intl.locale, transportOrder.external_order_created_date, getClientTimezone()),
        pickUpDate: toHumanReadableDate(intl.locale, pickupTimeWindow?.time_from, senderTimezone),
        from: toHumanReadableTime(intl.locale, pickupTimeWindow?.time_from, senderTimezone),
        to: toHumanReadableTime(intl.locale, pickupTimeWindow?.time_to, senderTimezone),
        deliveryDate: toHumanReadableDate(intl.locale, recipientTimeWindow?.time_from, recipientTimezone),
        senderName: senderName,
        senderCountry: senderAddress?.country,
        senderPostalCode: senderAddress?.postal_code,
        senderCity: senderAddress?.city,
        senderStreet: senderAddress?.street,
        senderDUNS: senderDuns,
        senderLNo: senderSupplierCode,
        buyerName: transportOrder.buyer?.name,
        buyerCountry: transportOrder.buyer?.address?.country,
        buyerZip: transportOrder.buyer?.address?.postal_code,
        buyerCity: transportOrder.buyer?.address?.city,
        buyerStreet: transportOrder.buyer?.address?.street,
        plantCustomer: recipientPlantCode,
        recipientCID3: shipment.isEndOfTransportChain ? endOfTransportChain.placeOfDischarge : undefined,
        unloadingPoint: getInternalPlaceOfDestination(articles),
        recipientName: recipientName,
        recipientCountry: recipientAddress?.country,
        recipientZip: recipientAddress?.postal_code,
        recipientCity: recipientAddress?.city,
        recipientStreet: recipientAddress?.street,
        productNumber, // This is broken, but VW confirmed that this is the way to go.
        lengthHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.length)?.value,
        widthHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.width)?.value,
        heightHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.height)?.value,
        numberOfPcc: undefined,
        innerPackagingMaterialId: undefined,
        handlingUnitQuantity: handlingUnit?.quantity,
        outerPackagingMaterialId: handlingUnit?.packaging_material_id,
        nrPackagingAid1: undefined,
        packagingAid1: undefined,
        nrPackagingAid2: undefined,
        packagingAid2: undefined,
        nrPackagingAid3: undefined,
        packagingAid3: undefined,
        unDangerousGoodsCode,
        descriptionDangerousGoods: undefined,
        dangerousGoodsClass: undefined,
        dangerousGoodsPackagingGroup: undefined,
        netExplosiveWeight: handlingUnit && aggregateNetExplosiveMassInG(handlingUnit),
        customsGoods: hasCustomsGoods(articles, intl),
        grossWeight: handlingUnit && calculateWeight(handlingUnit),
        grossVolume: handlingUnit && calculateVolume(handlingUnit),
        stackingFactor: handlingUnit?.stacking_factor,
        orderNumber: undefined,
        transportNumber: transportOrder.external_order_number
    }
}
